import React, { useEffect, useState, useRef, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import SurveyPreviewModal from '@/components/common/SurveyPreviewModal'
import { DesignConfig } from '@/api/surveySettingApi'
import { Template } from '@/types'
import { RootState, useAppSelector } from '@/store'
import { selectIsSuperAdmin } from '@/store/slices/authSlice'
import { useStoreData } from '@/hooks/useStoreData'

// Type definitions for PostMessage communication
interface SurveyDataPayload {
    type: 'SURVEY_DATA'
    payload: {
        surveyRules: any[]
        title: string
        description: string
        questions: Array<{
            questionId: string
            surveyId: string
            text: string
            description: string | null
            type:
            | 'single_select'
            | 'multi_select'
            | 'nps'
            | 'rating'
            | 'text'
            | 'date'
            | 'phone'
            | 'drop_down'
            | 'image'
            | 'content_block'
            isRequired: boolean
            orderIndex: number
            config: {
                options?: Array<{ id: string; text: string }>
                scale?: number
                minLabel?: string
                maxLabel?: string
                placeholder?: string
                randomize?: boolean
                allowOther?: boolean
                otherLabel?: string
                maxSelect?: number | null
                minSelect?: number | null
            }
            logic: Record<string, any>
        }>
        name: string
        thankYouCard?: {
            cardId: string
            surveyId: string
            cardType: 'welcome' | 'thankYou'
            messageType: 'default' | 'custom'
            blocks: Array<{
                blockId: string
                content: {
                    heading?: string
                    headingLevel?: number
                    text?: string
                    altText?: string
                    caption?: string
                    imageId?: string
                    imageUrl?: string
                    buttonText?: string
                    buttonAction?: string
                    code?: string
                    description?: string
                    expiryDate?: string
                    termsLink?: string
                }
                blockType:
                | 'heading'
                | 'text'
                | 'image'
                | 'button'
                | 'banner'
                | 'banner-image'
                | 'coupon_code'
                orderIndex: number
            }>
            layout: string
            isVisible: boolean
            status: string
            createdAt: string
            updatedAt: string
        } | null
    }
}


import {
    updateSurveyName,
    updateSurveyTitle,
    updateSurveyDescription,
    updateQuestions,
} from '@/store/slices/builder'
import { setCreateModal } from '@/store/slices/surveySlice'
import { Button } from '@/components/ui/button'
import {
    PencilIcon,
    CircleQuestionMark,
    Monitor,
    Smartphone,
    Undo2,
} from 'lucide-react'
import {
    CreateSurveyButton,
    SecondaryButton,
} from '@/features/survey-builder/Tabs/CustomButton'

import { FeatureAccess } from '@/components/FeatureAccess'
import { FEATURE_IDS } from '@/config/features'
import { Card, CardContent } from '@/components/ui/card'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogFooter,
} from '@/components/ui/dialog'
import { usePermission } from '@/hooks/usePermission'
import { PERMISSIONS } from '@/features/auth/constants'

// Helper function to add static URL prefix to image URLs for specific template
const addStaticUrlToImageQuestions = (
    questions: any[],
    surveyId: string | null | undefined
) => {
    const TARGET_TEMPLATE_ID = 'eb4853bb-694f-4c12-8686-5b1bbf83ab17'
    const imageUrl1 = '/builder/pack1.png'
    const imageUrl2 = '/builder/pack2.png'

    // Early return if surveyId is null, undefined, or doesn't match target
    if (!surveyId || surveyId !== TARGET_TEMPLATE_ID) {
        return questions
    }

    // Early return if questions is not an array
    if (!Array.isArray(questions)) {
        return questions
    }

    return questions.map((question: any) => {
        if (
            question?.type === 'image' &&
            question?.config?.options &&
            Array.isArray(question.config.options)
        ) {
            return {
                ...question,
                config: {
                    ...question.config,
                    options: question.config.options.map(
                        (option: any, index: number) => ({
                            ...option,
                            imageUrl: index === 0 ? imageUrl1 : imageUrl2,
                        })
                    ),
                },
            }
        }
        return question
    })
}

interface CreateSurveyPreviewWrapperProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    survey: Template | null
    designSettings?: DesignConfig | null
    needsStoreGate?: boolean
    onStoreRequired?: () => void
    selectedChannel?: string
}

const CreateSurveyPreviewWrapper: React.FC<CreateSurveyPreviewWrapperProps> = ({
    open,
    onOpenChange,
    survey,
    designSettings,
    needsStoreGate = false,
    onStoreRequired,
    selectedChannel,
}) => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { data: _storeData } = useStoreData()
    const canEditTemplate = usePermission(PERMISSIONS.TEMPLATES_WRITE)
    const isSuperAdmin = useAppSelector(selectIsSuperAdmin)
    const [isMobileView, setIsMobileView] = useState(false)
    const iframeRef = useRef<HTMLIFrameElement>(null)
    const { questions, name, title, description, surveyRules, thankYouCard } =
        useSelector((state: RootState) => state.builder)

    // Send restart event to iframe
    const sendRestartEvent = useCallback(() => {
        if (!iframeRef.current || !iframeRef.current.contentWindow) return
        iframeRef.current.contentWindow.postMessage(
            { type: 'RESTART_SURVEY' },
            '*'
        )
    }, [])

    // Check if we should show the onsite preview (iframe-based)
    const shouldShowOnsitePreview =
        survey?.channels?.['on-site']?.enabled &&
        survey?.channels?.['on-site']?.distributionMethod === 'popup'

    // Check if we should show the iframe preview instead of the modal
    // Based on the same logic as PreviewWrapper but using selectedChannel instead of channelData
    const shouldShowIframePreview =
        survey?.channels?.web?.enabled ||
        (survey?.channels?.pps?.enabled &&
            survey?.channels?.pps?.distributionMethod === 'embedded-link')

    // Populate builder state with template data when showing iframe preview
    useEffect(() => {
        if (
            (shouldShowIframePreview || shouldShowOnsitePreview) &&
            survey &&
            open
        ) {
            // Process questions to add static URL prefix for specific template
            const processedQuestions = addStaticUrlToImageQuestions(
                survey.questions,
                survey.surveyId
            )

            // Map template questions to builder format (add missing properties)
            const builderQuestions = processedQuestions.map(
                (question: any) => ({
                    ...question,
                    isVisible: true, // Add missing isVisible property
                    inlineImage: question.inlineImage || null,
                    logic: question.logic || {},
                })
            )

            // Update builder state with template data so SurveyPreviewIframe can access it
            dispatch(updateSurveyName(survey.name))
            dispatch(updateSurveyTitle(survey.name))
            dispatch(updateSurveyDescription(survey.description || ''))
            dispatch(updateQuestions({ questions: builderQuestions }))
        }
    }, [
        shouldShowIframePreview,
        shouldShowOnsitePreview,
        survey,
        open,
        dispatch,
    ])

    // Map builder questions to iframe format
    const mapQuestionsToIframeFormat = useCallback(() => {
        return questions.map((question: any) => ({
            questionId: question.questionId,
            surveyId: question.surveyId,
            text: question.text,
            description: question.description,
            type: question.type as any,
            inlineImage: question.inlineImage,
            contentBlocks: question.contentBlocks,
            isRequired: question.isRequired,
            orderIndex: question.orderIndex,
            config: question.config,
            logic: question.logic,
        }))
    }, [questions])

    // Map design settings to iframe format
    const mapDesignToIframeFormat = useCallback(() => {
        if (!designSettings) return null

        return {
            questionText: {
                fontSize: designSettings.questionText.fontSize,
                color: designSettings.questionText.color,
                fontWeight: designSettings.questionText.fontWeight,
            },
            answerText: {
                fontSize: designSettings.answerText.fontSize,
                color: designSettings.answerText.color,
                fontWeight: designSettings.answerText.fontWeight,
            },
            navigationButtons: {
                back: {
                    label: designSettings.navigationButtons.back.label,
                    fontSize: designSettings.navigationButtons.back.fontSize,
                    fontWeight:
                        designSettings.navigationButtons.back.fontWeight,
                    height: designSettings.navigationButtons.back.height,
                },
                next: {
                    label: designSettings.navigationButtons.next.label,
                    fontSize: designSettings.navigationButtons.next.fontSize,
                    fontWeight:
                        designSettings.navigationButtons.next.fontWeight,
                    textColor: designSettings.navigationButtons.next.textColor,
                    borderRadius:
                        designSettings.navigationButtons.next.borderRadius,
                    bgColor: designSettings.navigationButtons.next.bgColor,
                    height: designSettings.navigationButtons.next.height,
                },
                submit: {
                    label: designSettings.navigationButtons.submit.label,
                    fontSize: designSettings.navigationButtons.submit.fontSize,
                    fontWeight:
                        designSettings.navigationButtons.submit.fontWeight,
                    height: designSettings.navigationButtons.submit.height,
                },
            },
            appBackground: {
                color: designSettings.appBackground.color,
            },
            logo: {
                url: designSettings.logo.url,
                width: designSettings.logo.width,
                height: designSettings.logo.height,
                alignment: designSettings.logo.alignment,
            },
            theme: {
                mode:
                    designSettings.theme.mode === 'custom'
                        ? 'light'
                        : designSettings.theme.mode,
                textColor: designSettings.theme.textColor,
                fontFamily: designSettings.theme.fontFamily,
                primaryColor: designSettings.theme.primaryColor,
                secondaryColor: designSettings.theme.secondaryColor,
            },
            banner: {
                url: designSettings.banner.url,
                height: designSettings.banner.height,
                borderRadius: designSettings.banner.borderRadius,
                backgroundColor: designSettings.banner.backgroundColor,
            },
        }
    }, [designSettings])

    // Send survey data to iframe
    const sendSurveyData = useCallback(() => {
        if (!iframeRef.current || !iframeRef.current.contentWindow) return

        const surveyData: SurveyDataPayload = {
            type: 'SURVEY_DATA',
            payload: {
                questions: mapQuestionsToIframeFormat(),
                surveyRules: surveyRules,
                title: title,
                description: description,
                name: title || name || survey?.name || 'Untitled Survey',
                thankYouCard: thankYouCard,
            },
        }

        iframeRef.current.contentWindow.postMessage(surveyData, '*')
        console.log(
            'Sent survey data to ' +
            (shouldShowOnsitePreview ? 'onsite ' : '') +
            'iframe:',
            surveyData
        )
    }, [
        mapQuestionsToIframeFormat,
        title,
        name,
        survey?.name,
        surveyRules,
        description,
        thankYouCard,
        shouldShowOnsitePreview,
    ])

    // Send customization data to iframe
    const sendCustomizationData = useCallback(() => {
        if (!iframeRef.current || !iframeRef.current.contentWindow) return

        const customizationData = mapDesignToIframeFormat()
        if (!customizationData) return

        const payload = {
            type: 'CUSTOMIZATION_DATA',
            payload: customizationData,
        }

        iframeRef.current.contentWindow.postMessage(payload, '*')
        console.log(
            'Sent customization data to ' +
            (shouldShowOnsitePreview ? 'onsite ' : '') +
            'iframe:',
            payload
        )
    }, [mapDesignToIframeFormat, shouldShowOnsitePreview])

    // Handle iframe load event
    const handleIframeLoad = useCallback(() => {
        console.log('Iframe loaded, sending data...')
        // Send data after a short delay to ensure iframe is ready
        setTimeout(() => {
            sendSurveyData()
            sendCustomizationData()
        }, 500)
    }, [sendSurveyData, sendCustomizationData])

    // Handle messages from iframe
    useEffect(() => {
        if (!shouldShowIframePreview && !shouldShowOnsitePreview) return

        const handleMessage = (event: MessageEvent) => {
            console.log(
                'Received message from ' +
                (shouldShowOnsitePreview ? 'onsite ' : '') +
                'iframe:',
                event.data
            )

            switch (event.data.type) {
                case 'REQUEST_SURVEY_DATA':
                    sendSurveyData()
                    break
                case 'REQUEST_CUSTOMIZATION_DATA':
                    sendCustomizationData()
                    break
                default:
                    break
            }
        }

        window.addEventListener('message', handleMessage)
        return () => window.removeEventListener('message', handleMessage)
    }, [sendSurveyData, sendCustomizationData])

    // Send data when questions or design settings change
    useEffect(() => {
        if (
            (shouldShowIframePreview || shouldShowOnsitePreview) &&
            iframeRef.current &&
            iframeRef.current.contentWindow
        ) {
            sendSurveyData()
        }
    }, [
        shouldShowIframePreview,
        shouldShowOnsitePreview,
        questions,
        title,
        name,
        thankYouCard,
        sendSurveyData,
    ])

    useEffect(() => {
        if (
            (shouldShowIframePreview || shouldShowOnsitePreview) &&
            iframeRef.current &&
            iframeRef.current.contentWindow &&
            designSettings
        ) {
            sendCustomizationData()
        }
    }, [
        shouldShowIframePreview,
        shouldShowOnsitePreview,
        designSettings,
        sendCustomizationData,
    ])

    if (shouldShowOnsitePreview) {
        return (
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent
                    className="w-[90vw] h-[90vh] max-w-6xl max-h-[800px] p-0 overflow-hidden flex flex-col"
                    hideClose={true}
                >
                    <DialogHeader className="flex-shrink-0 p-4 border-b">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-[16px] font-[650] text-[#303030]">
                                    {survey?.name || 'Untitled Survey'}
                                </h3>
                                <div className="text-[14px] flex items-center gap-1 font-[450] text-[#616161]">
                                    <CircleQuestionMark
                                        className="w-4 h-4 text-[#616161]"
                                        strokeWidth={2}
                                    />
                                    {survey?.questions?.length || 0} Questions
                                </div>
                            </div>
                            <div className="flex items-center gap-2 mr-2">
                                <SecondaryButton
                                    icon={<Undo2 className="w-4 h-4 mr-1" />}
                                    text="Restart"
                                    onClick={sendRestartEvent}
                                />
                                <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 mr-4">
                                    <Button
                                        variant={
                                            !isMobileView ? 'outline' : 'ghost'
                                        }
                                        size="sm"
                                        onClick={() => setIsMobileView(false)}
                                        className={`h-8 px-3 ${!isMobileView
                                            ? 'bg-white shadow-sm !hover:bg-gray-200'
                                            : 'text-gray-600'
                                            }`}
                                    >
                                        <Monitor className="w-4 h-4" />
                                    </Button>
                                    <Button
                                        variant={
                                            isMobileView ? 'outline' : 'ghost'
                                        }
                                        size="sm"
                                        onClick={() => setIsMobileView(true)}
                                        className={`h-8 px-3 ${isMobileView
                                            ? 'bg-white shadow-sm !hover:bg-gray-200'
                                            : 'text-gray-600 '
                                            }`}
                                    >
                                        <Smartphone className="w-4 h-4" />
                                    </Button>
                                </div>
                                <button
                                    onClick={() => onOpenChange(false)}
                                    className="text-gray-500 hover:text-gray-700 text-lg"
                                >
                                    ✕
                                </button>
                            </div>
                        </div>
                    </DialogHeader>

                    <div className="flex-1 overflow-hidden px-4">
                        <div
                            className={`bg-[#FFFFFF] rounded-xl overflow-hidden mx-auto transition-all duration-300 ${isMobileView ? 'w-[310px]' : 'w-full'
                                }`}
                        >
                            <iframe
                                ref={iframeRef}
                                src={`${import.meta.env.VITE_SURVEY_PREVIEW_URL}/onsite-preview`}
                                className="w-full h-[70vh] max-h-[600px]"
                                title="Onsite Survey Preview"
                                frameBorder="0"
                                onLoad={handleIframeLoad}
                                sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                                allow="display-capture"
                            />
                        </div>
                    </div>

                    <DialogFooter className="flex-shrink-0 p-4 border-t bg-white">
                        <div className="w-full flex items-center justify-between gap-2">
                            <SecondaryButton
                                text="Back"
                                onClick={() => onOpenChange(false)}
                            />
                            <div className="flex items-center gap-2">
                                {isSuperAdmin && survey && (
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={() => {
                                            dispatch(setCreateModal(null))
                                            navigate(
                                                `/builder/${survey.surveyId}?isTemplate=true`
                                            )
                                        }}
                                    >
                                        <PencilIcon className="h-4 w-4" />
                                    </Button>
                                )}
                                {survey && (
                                    <FeatureAccess
                                        featureId={FEATURE_IDS.QUESTIONS_LIMIT}
                                        fallback={
                                            <CreateSurveyButton
                                                text="Use Template"
                                                onClick={() => {
                                                    if (needsStoreGate && onStoreRequired) {
                                                        onStoreRequired()
                                                    }
                                                }}
                                            />
                                        }
                                        currentLimit={
                                            survey?.questions?.length
                                                ? survey.questions.length - 1
                                                : 0
                                        }
                                    >
                                        <CreateSurveyButton
                                            text="Use Template"
                                            onClick={() => {
                                                if (needsStoreGate && onStoreRequired) {
                                                    onStoreRequired()
                                                    return
                                                }
                                                dispatch(setCreateModal(null))
                                                navigate(
                                                    `/surveys/template/${survey.surveyId}`
                                                )
                                            }}
                                        />
                                    </FeatureAccess>
                                )}
                            </div>
                        </div>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        )
    }

    if (shouldShowIframePreview) {
        return (
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent
                    className="w-[90vw] h-[90vh] max-w-6xl max-h-[800px] p-0 overflow-hidden flex flex-col"
                    hideClose={true}
                >
                    <DialogHeader className="flex-shrink-0 p-4 border-b">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-[16px] font-[650] text-[#303030]">
                                    {survey?.name || 'Untitled Survey'}
                                </h3>
                                <div className="text-[14px] flex items-center gap-1 font-[450] text-[#616161]">
                                    <CircleQuestionMark
                                        className="w-4 h-4 text-[#616161]"
                                        strokeWidth={2}
                                    />
                                    {survey?.questions?.length || 0} Questions
                                </div>
                            </div>
                            <div className="flex items-center gap-2 mr-2">
                                <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 mr-4">
                                    <Button
                                        variant={
                                            !isMobileView ? 'outline' : 'ghost'
                                        }
                                        size="sm"
                                        onClick={() => setIsMobileView(false)}
                                        className={`h-8 px-3 ${!isMobileView
                                            ? 'bg-white shadow-sm !hover:bg-gray-200'
                                            : 'text-gray-600'
                                            }`}
                                    >
                                        <Monitor className="w-4 h-4" />
                                    </Button>
                                    <Button
                                        variant={
                                            isMobileView ? 'outline' : 'ghost'
                                        }
                                        size="sm"
                                        onClick={() => setIsMobileView(true)}
                                        className={`h-8 px-3 ${isMobileView
                                            ? 'bg-white shadow-sm !hover:bg-gray-200'
                                            : 'text-gray-600 '
                                            }`}
                                    >
                                        <Smartphone className="w-4 h-4" />
                                    </Button>
                                </div>
                                <button
                                    onClick={() => onOpenChange(false)}
                                    className="text-gray-500 hover:text-gray-700 text-lg"
                                >
                                    ✕
                                </button>
                            </div>
                        </div>
                    </DialogHeader>

                    {/* Main content area */}
                    <div className="flex-1 overflow-hidden p-6">
                        <Card className="h-full border-none">
                            <CardContent className="p-4 bg-accent rounded-xl border-[#E5E5E5] border h-full overflow-hidden">
                                <div
                                    className={`h-full bg-[#FFFFFF] rounded-xl overflow-hidden mx-auto transition-all duration-300 ${isMobileView ? 'w-[405px]' : 'w-full'
                                        }`}
                                >
                                    <iframe
                                        ref={iframeRef}
                                        src={`${import.meta.env.VITE_SURVEY_PREVIEW_URL}/preview-dashboard`}
                                        className="w-full h-full"
                                        title="Survey Preview"
                                        frameBorder="0"
                                        onLoad={handleIframeLoad}
                                        sandbox="allow-scripts allow-same-origin allow-forms"
                                    />
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    <DialogFooter className="flex-shrink-0 p-4 border-t bg-white">
                        <div className="w-full flex items-center justify-between gap-2">
                            <SecondaryButton
                                text="Back"
                                onClick={() => onOpenChange(false)}
                            />
                            <div className="flex items-center gap-2">
                                {isSuperAdmin && survey && (
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={() => {
                                            dispatch(setCreateModal(null))
                                            navigate(
                                                `/builder/${survey.surveyId}?isTemplate=true`
                                            )
                                        }}
                                    >
                                        <PencilIcon className="h-4 w-4" />
                                    </Button>
                                )}
                                {survey && (
                                    <FeatureAccess
                                        featureId={FEATURE_IDS.QUESTIONS_LIMIT}
                                        fallback={
                                            <CreateSurveyButton
                                                text="Use Template"
                                                onClick={() => {
                                                    if (needsStoreGate && onStoreRequired) {
                                                        onStoreRequired()
                                                    }
                                                }}
                                            />
                                        }
                                        currentLimit={
                                            survey?.questions?.length
                                                ? survey.questions.length - 1
                                                : 0
                                        }
                                    >
                                        <CreateSurveyButton
                                            text="Use Template"
                                            onClick={() => {
                                                if (needsStoreGate && onStoreRequired) {
                                                    onStoreRequired()
                                                    return
                                                }
                                                dispatch(setCreateModal(null))
                                                navigate(
                                                    `/surveys/template/${survey.surveyId}`
                                                )
                                            }}
                                        />
                                    </FeatureAccess>
                                )}
                            </div>
                        </div>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        )
    }

    // Default to regular preview modal for app-block pps and other channels
    return (
        <SurveyPreviewModal
            open={open}
            onOpenChange={onOpenChange}
            survey={survey}
            needsStoreGate={needsStoreGate}
            onStoreRequired={onStoreRequired}
        />
    )
}

export default CreateSurveyPreviewWrapper
